import { getItemsInRow, getVirtuosoHeight } from '@/utils/virtuosoUtils';
import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { GridComponents, GridListProps, VirtuosoGrid, VirtuosoGridProps } from 'react-virtuoso';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Spinner } from '@shopify/polaris';
/**
 * Please do not remove the initialItemCount prop, it is used to resolve the issue of the grid not showing items sometimes.
 * Please make sure that the items that are rendered all have the same height, otherwise the grid will flicker on scroll.
 */

export type MediaGridSize = 'small' | 'medium' | 'large';
type Props<T extends { id?: string }> = {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  size?: MediaGridSize;
  gap?: string;
  className?: string;
  staticHeight?: number | string;
  style?: React.CSSProperties;
  endReached?: () => void;
  isFetchingNextPage?: boolean;
};

const getWidth = (size: MediaGridSize) => {
  switch (size) {
    case 'small':
      return 100;
    case 'medium':
      return 120;
    case 'large':
      return 174;
    default:
      return 174;
  }
};

export const getItemHeight = (size: MediaGridSize) => {
  const width = getWidth(size);
  return Math.ceil(width * 2.5);
};

const MediaGrid = <T extends { id?: string }>({
  items,
  renderItem,
  size = 'large',
  gap = '16',
  className,
  style,
  staticHeight,
  endReached,
  isFetchingNextPage,
}: Props<T>) => {
  const itemWidth = getWidth(size);
  const itemHeight = getItemHeight(size);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const firstGridRender = useRef(true);
  const finishedTopScroll = useRef(false);

  const { ref: inViewRef, inView } = useInView();

  const [gridWidth, setGridWidth] = useState(0);

  useEffect(() => {
    if (inView && scrollRef.current?.clientWidth) {
      setGridWidth(scrollRef.current.clientWidth);
    }
  }, [scrollRef.current?.clientWidth, inView]);

  const { height, initialItemCount, itemsInRow } = useMemo(() => {
    if (staticHeight) {
      return {
        height: staticHeight,
        initialItemCount: items?.length || 0,
        itemsInRow: getItemsInRow(gridWidth, itemWidth, gap),
      };
    }

    return getVirtuosoHeight(items?.length, gridWidth, itemWidth, gap);
  }, [staticHeight, itemWidth, gap, items?.length, gridWidth]);

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(items.length / itemsInRow),
    getScrollElement: () => scrollRef.current,
    estimateSize: () => itemHeight,
    overscan: itemsInRow * 2,
    gap: parseInt(gap),
    useScrollendEvent: true,
    enabled: !!gridWidth && !!height,
  });

  const isAtBottom = useCallback(() => {
    if (!rowVirtualizer.scrollElement) return false;
    const { scrollTop, scrollHeight, clientHeight } = rowVirtualizer.scrollElement;
    return Math.abs(scrollHeight - clientHeight - scrollTop) < itemHeight;
  }, [
    rowVirtualizer.scrollElement?.scrollTop,
    rowVirtualizer.scrollElement?.scrollHeight,
    rowVirtualizer.scrollElement?.clientHeight,
    itemHeight,
  ]);

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: itemsInRow,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => itemWidth,
    overscan: itemsInRow,
    gap: parseInt(gap),
    enabled: !!gridWidth && !!height,
  });

  useEffect(() => {
    if (endReached && isAtBottom()) {
      endReached();
    }
  }, [endReached, isAtBottom]);

  const renderGrid = () => {
    return (
      <>
        {!!gridWidth && !!height && (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: `${columnVirtualizer.getTotalSize()}px`,
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => (
              <React.Fragment key={virtualRow.key}>
                {columnVirtualizer.getVirtualItems().map((virtualColumn) => {
                  const itemIndex = virtualRow.index * itemsInRow + virtualColumn.index;
                  const item = items[itemIndex];

                  if (!item) return null;

                  return (
                    <div
                      key={virtualColumn.key}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: `${virtualColumn.size}px`,
                        height: `${virtualRow.size}px`,
                        transform: `translateX(${virtualColumn.start}px) translateY(${virtualRow.start}px)`,
                      }}
                    >
                      {renderItem(item)}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
            {isFetchingNextPage && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Spinner size="large" />
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div ref={inViewRef} style={{ height: '100%', width: '100%' }}>
      <div
        ref={scrollRef}
        style={{
          height,
          width: '100%',
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {renderGrid()}
      </div>
    </div>
  );
};

export default memo(MediaGrid) as <T extends { id?: string }>(props: Props<T>) => JSX.Element;
